import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import VideoPicker from '../components/VideoPicker';

export default function Library() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    setVideos(await api.listVideos());
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const del = async (v) => {
    if (!confirm(`Delete "${v.label || v.original_filename}"? This cannot be undone.`)) return;
    await api.deleteVideo(v.id);
    load();
  };

  return (
    <div className="max-w-[1200px] mx-auto p-4">
      <div className="flex items-center justify-between pb-3 border-b border-fairway-800 mb-6">
        <div>
          <h1 className="display text-2xl text-cream-100">Your swing library</h1>
          <p className="text-xs text-ink-400 mt-0.5">All videos you've uploaded.</p>
        </div>
        <button
          onClick={() => setPickerOpen(true)}
          className="px-4 py-2 bg-cream-100 text-fairway-950 rounded text-sm font-medium hover:bg-cream-200 transition"
        >
          + Upload video
        </button>
      </div>

      {loading ? (
        <div className="text-ink-400 text-sm">Loading…</div>
      ) : videos.length === 0 ? (
        <div className="text-center py-16">
          <p className="display text-lg text-ink-200">Nothing here yet.</p>
          <p className="text-sm text-ink-400 mt-2">Upload your first swing to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {videos.map(v => (
            <div
              key={v.id}
              className="bg-fairway-900 border border-fairway-800 rounded-lg overflow-hidden"
            >
              <div className="aspect-[9/16] bg-black relative">
                <video
                  src={api.streamUrl(v.r2_key)}
                  preload="metadata"
                  muted
                  className="w-full h-full object-contain"
                />
                <div className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/70 rounded text-[10px] monospace text-ink-200">
                  {v.duration_seconds ? v.duration_seconds.toFixed(1) + 's' : '—'}
                </div>
              </div>
              <div className="p-3 space-y-1">
                <div className="text-sm text-cream-100 truncate font-medium">
                  {v.label || v.original_filename || 'Untitled'}
                </div>
                <div className="text-[11px] text-ink-400 flex gap-2">
                  {v.club && <span>{v.club}</span>}
                  {v.view_angle && <span>· {v.view_angle}</span>}
                </div>
                <div className="text-[10px] text-ink-500 monospace">
                  {new Date(v.created_at).toLocaleDateString()}
                </div>
                <div className="flex gap-2 pt-2 border-t border-fairway-800 mt-2">
                  <button
                    onClick={() => navigate(`/analyze?a=${encodeURIComponent(v.r2_key)}`)}
                    className="flex-1 px-2 py-1 text-[10px] uppercase tracking-wider bg-fairway-800 hover:bg-fairway-700 rounded transition"
                  >
                    Compare
                  </button>
                  <button
                    onClick={() => setEditing(v)}
                    className="px-2 py-1 text-[10px] uppercase tracking-wider bg-fairway-800 hover:bg-fairway-700 rounded transition"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => del(v)}
                    className="px-2 py-1 text-[10px] uppercase tracking-wider text-accent-flag hover:bg-accent-flag/10 rounded transition"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {pickerOpen && (
        <VideoPicker
          slot="new"
          onClose={() => setPickerOpen(false)}
          onPick={() => { setPickerOpen(false); load(); }}
        />
      )}
      {editing && (
        <EditVideoModal
          video={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function EditVideoModal({ video, onClose, onSaved }) {
  const [label, setLabel] = useState(video.label || '');
  const [club, setClub] = useState(video.club || '');
  const [viewAngle, setViewAngle] = useState(video.view_angle || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.updateVideo(video.id, { label, club, view_angle: viewAngle });
      onSaved();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-fairway-900 border border-fairway-700 rounded-lg w-full max-w-md p-5 space-y-3"
      >
        <h2 className="display text-lg text-cream-100">Edit video</h2>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-ink-400 block mb-1">Label</span>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="w-full px-3 py-1.5 bg-fairway-950 border border-fairway-700 rounded text-sm text-ink-100 focus:outline-none focus:border-cream-300"
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-ink-400 block mb-1">Club</span>
            <select
              value={club}
              onChange={(e) => setClub(e.target.value)}
              className="w-full px-3 py-1.5 bg-fairway-950 border border-fairway-700 rounded text-sm text-ink-100 focus:outline-none focus:border-cream-300"
            >
              <option value="">—</option>
              <option>Driver</option>
              <option>3-wood</option>
              <option>Hybrid</option>
              <option>Long iron</option>
              <option>Mid iron</option>
              <option>Short iron</option>
              <option>Wedge</option>
              <option>Putter</option>
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-ink-400 block mb-1">View</span>
            <select
              value={viewAngle}
              onChange={(e) => setViewAngle(e.target.value)}
              className="w-full px-3 py-1.5 bg-fairway-950 border border-fairway-700 rounded text-sm text-ink-100 focus:outline-none focus:border-cream-300"
            >
              <option value="">—</option>
              <option>Down-the-line</option>
              <option>Face-on</option>
              <option>Overhead</option>
            </select>
          </label>
        </div>
        <div className="flex gap-2 pt-2">
          <button onClick={onClose} className="flex-1 px-3 py-1.5 bg-fairway-800 rounded text-xs uppercase tracking-wider hover:bg-fairway-700 transition">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="flex-1 px-3 py-1.5 bg-cream-100 text-fairway-950 rounded text-xs uppercase tracking-wider hover:bg-cream-200 disabled:opacity-30 transition"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
